import { useEffect, useState } from 'react';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { multiply, round } from 'mathjs';

import { dataGridPremiumColumns, parseNumberFromText } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AutoCompleteSelectUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
} from '../Interface';
import { formatCurrency } from '../../utils/currencyHelper';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

function EditToolbar(props) {
  const { disabled, handleClick, push } = props;

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={() => handleClick(push)} />
    </GridToolbarContainer>
  );
}

const BudgetLine = ({ disabled, defaultBudgetLine, autoCompleteOptions, autocompleteData }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [pendingUpdates, setPendingUpdates] = useState([]);

  useEffect(() => {
    if (pendingUpdates.length === 0) {
      // Clean empty rows only if the array exists and changed
      const bl = values?.budgetLine;
      if (!Array.isArray(bl)) return;
      const cleaned = bl.filter((r) => !!r?.itemCode);
      if (cleaned.length !== bl.length) {
        setFieldValue('budgetLine', cleaned);
      }
      return;
    }

    const process = async () => {
      const [next, ...rest] = pendingUpdates;
      const { newIndex, itemCode, name, kbt, unit, qty, unitPrice } = next;

      setFieldValue(`budgetLine.${newIndex}.itemCode`, itemCode ?? null);
      const foundItem = autocompleteData?.items?.find((i) => i.itemCode === itemCode);
      setFieldValue(`budgetLine.${newIndex}.itemName`, foundItem?.itemName ?? '');

      setFieldValue(`budgetLine.${newIndex}.name`, name);
      setFieldValue(`budgetLine.${newIndex}.kbt`, kbt);
      setFieldValue(`budgetLine.${newIndex}.unit`, unit);

      const q = qty ?? '';
      const u = unitPrice ?? '';

      setFieldValue(`budgetLine.${newIndex}.qty`, q);
      setFieldValue(`budgetLine.${newIndex}.unitPrice`, u);

      const sum = calculateSumPrice(q, u);
      setFieldValue(`budgetLine.${newIndex}.sumPrice`, sum);
      setPendingUpdates(rest);
    };

    process();
  }, [pendingUpdates]);

  const createNewBudgetLineData = () => ({
    ...defaultBudgetLine,
    id: uuidv4(),
  });

  const handleClipboardPaste = async (params, insert) => {
    const { data } = params;

    const startIndex = values.budgetLine.length;
    let successOffset = 0;

    for (const row of data) {
      const [itemCode, name, kbt, unit, qty, unitPrice] = row;

      const item = autocompleteData?.items?.find((i) => i.itemCode === itemCode);
      if (!item) {
        addNotification({
          content: `A(z) ${itemCode} feladat típus nem létezik!`,
          type: NotificationType.ERROR,
        });
        continue;
      }

      const q = parseNumberFromText(qty);
      if (isNaN(q)) {
        addNotification({
          content: `A mennyiség megadása kötelező a(z) ${itemCode} feladat típushoz!`,
          type: NotificationType.ERROR,
        });
        continue;
      }

      const u = parseNumberFromText(unitPrice);
      if (isNaN(u)) {
        addNotification({
          content: `Az egységár megadása kötelező a(z) ${itemCode} feladat típushoz!`,
          type: NotificationType.ERROR,
        });
        continue;
      }

      const newLine = createNewBudgetLineData();

      const newIndex = startIndex + successOffset;
      successOffset += 1;

      insert(newIndex, newLine);

      setPendingUpdates((prev) => [...prev, { newIndex, itemCode, name, kbt, unit, qty: q, unitPrice: u }]);
    }
  };

  const handleClick = async (push) => {
    const newField = createNewBudgetLineData();

    await push(newField);

    const fieldsToValidate = [
      `budgetLine.${values.budgetLine.length ?? 0}.itemCode`,
      `budgetLine.${values.budgetLine.length ?? 0}.itemName`,
      `budgetLine.${values.budgetLine.length ?? 0}.name`,
      `budgetLine.${values.budgetLine.length ?? 0}.kbt`,
      `budgetLine.${values.budgetLine.length ?? 0}.qty`,
      `budgetLine.${values.budgetLine.length ?? 0}.unitPrice`,
      `budgetLine.${values.budgetLine.length ?? 0}.sumPrice`,
      `budgetLine.${values.budgetLine.length ?? 0}.unit`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }

    return defaultBudgetLine.id;
  };

  const currency = autocompleteData?.currency?.find((c) => c.code === values.currencyCode);
  const amountRounding = currency?.amountRounding ?? 0;

  const getValueIndex = (values, id) => (values?.budgetLine ?? []).findIndex((rc) => rc.id === id);

  const calculateSumPrice = (qty, unitPrice) => {
    const q = Number(qty);
    const uPrice = Number(unitPrice);

    if (!Number.isFinite(q) || !Number.isFinite(uPrice)) return '';

    const raw = multiply(q, uPrice);
    return round(raw, amountRounding);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'itemCode',
        headerName: 'Feladat típus',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const findedItem = autoCompleteOptions?.items.find((item) => item.value === data.row?.itemCode);
          return findedItem?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = autoCompleteOptions.items.find((item) => item.value === data.row.itemCode);

          return (
            <Field
              name={`budgetLine.${valueIndex}.itemCode`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.budgetLine?.[valueIndex]?.itemCode} />}
              selectedValue={values.budgetLine?.[valueIndex]?.itemCode}
              selectedLabelValue={findedItem?.label}
              onChange={(_e, newVal) => {
                const foundItem = autocompleteData?.items?.find((item) => item.itemCode === newVal?.value);
                setFieldValue(`budgetLine.${valueIndex}.itemCode`, newVal?.value ?? null);

                if (foundItem) {
                  setFieldValue(`budgetLine.${valueIndex}.itemName`, foundItem.itemName);
                } else {
                  setFieldValue(`budgetLine.${valueIndex}.itemName`, '');
                }

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal.value ?? null,
                });
              }}
              dataset={autoCompleteOptions.items}
              onBlur={async () => await validateField(`budgetLine.${valueIndex}.itemCode`)}
            />
          );
        },
      },
      {
        field: 'name',
        headerName: 'Megnevezés',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`budgetLine.${valueIndex}.name`}
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.budgetLine?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`budgetLine.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`budgetLine.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'kbt',
        headerName: 'KBT azonosító',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`budgetLine.${valueIndex}.kbt`}
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.budgetLine?.[valueIndex]?.kbt} />}
              onChange={(e) => {
                setFieldValue(`budgetLine.${valueIndex}.kbt`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`budgetLine.${valueIndex}.kbt`)}
            />
          );
        },
      },
      {
        field: 'unit',
        headerName: 'Mért. e.',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`budgetLine.${valueIndex}.unit`}
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.budgetLine?.[valueIndex]?.unit} />}
              onChange={(e) => {
                setFieldValue(`budgetLine.${valueIndex}.unit`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`budgetLine.${valueIndex}.unit`)}
            />
          );
        },
      },
      {
        field: 'qty',
        headerName: 'Mennyiség',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`budgetLine.${valueIndex}.qty`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.budgetLine?.[valueIndex]?.qty} />}
              onChange={(e) => {
                const qty = e.target.value ?? '';
                const unitPrice = values.budgetLine?.[valueIndex]?.unitPrice ?? '';

                setFieldValue(`budgetLine.${valueIndex}.qty`, qty);

                const sum = calculateSumPrice(qty, unitPrice);
                setFieldValue(`budgetLine.${valueIndex}.sumPrice`, sum);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`budgetLine.${valueIndex}.qty`)}
            />
          );
        },
      },
      {
        field: 'unitPrice',
        headerName: 'Egységár',
        type: 'string',
        editable: true,
        valueGetter: (params) => {
          const n = Number(params);
          return Number.isFinite(n) ? n : null;
        },
        valueFormatter: (p) => (p == null ? '' : formatCurrency(p)),
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`budgetLine.${valueIndex}.unitPrice`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.budgetLine?.[valueIndex]?.unitPrice} />}
              onChange={(e) => {
                const unitPrice = e.target.value ?? '';
                const qty = values.budgetLine?.[valueIndex]?.qty ?? '';

                setFieldValue(`budgetLine.${valueIndex}.unitPrice`, unitPrice);

                const sum = calculateSumPrice(qty, unitPrice);
                setFieldValue(`budgetLine.${valueIndex}.sumPrice`, sum);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`budgetLine.${valueIndex}.unitPrice`)}
            />
          );
        },
      },
      {
        field: 'sumPrice',
        headerName: 'Ajánlati ár',
        type: 'number',
        aggregable: true,
        editable: false,
        valueGetter: (params) => {
          const n = Number(params);
          return Number.isFinite(n) ? n : 0;
        },
        valueFormatter: (params) => formatCurrency(params ?? 0),
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`budgetLine.${valueIndex}.sumPrice`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.budgetLine?.[valueIndex]?.sumPrice} />}
              onChange={(e) => {
                setFieldValue(`budgetLine.${valueIndex}.sumPrice`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`budgetLine.${valueIndex}.sumPrice`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: (params) => {
          const idStr = String(params.id ?? '');

          if (idStr.startsWith('auto-generated-')) return [];

          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, params.id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="budgetLine">
            {({ remove, push, insert }) => (
              <DatagridPremiumUi
                datagridInitialState={{
                  density: 'compact',
                }}
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.BudgetLine}
                height={400}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.budgetLine ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultBudgetLine,
                    handleClick,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
                onClipboardPasteStart={(params) => handleClipboardPaste(params, insert)}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default BudgetLine;
